import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data.user);
        setTenant(r.data.tenant);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data.user);
    setTenant(r.data.tenant);
    return r.data;
  }, []);

  const register = useCallback(async (payload) => {
    const r = await api.post("/auth/register", payload);
    setUser(r.data.user);
    setTenant(r.data.tenant);
    return r.data;
  }, []);

  const loginRequestor = useCallback(async (email, password, tenantId) => {
    const r = await api.post("/auth/requestor-login", { email, password, ...(tenantId?.trim() ? { tenant_id: tenantId.trim() } : {}) });
    setUser(r.data.user);
    setTenant(r.data.tenant);
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed — clearing local session anyway", e);
    }
    setUser(null);
    setTenant(null);
  }, []);

  const refreshSession = useCallback(async () => {
    const r = await api.get("/auth/me");
    setUser(r.data.user);
    setTenant(r.data.tenant);
  }, []);

  const hasModule = useCallback((m) => !!tenant?.enabled_modules?.includes(m), [tenant]);

  const value = useMemo(
    () => ({ user, tenant, loading, login, register, loginRequestor, logout, refreshSession, hasModule }),
    [user, tenant, loading, login, register, loginRequestor, logout, refreshSession, hasModule]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
